import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, LogIn, ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useAuth } from "../../context/AuthContext";
import { forgotPassword } from "../../lib/api";
import logo from "../../assets/logo-transparent.png";
import { toast } from "sonner";

export default function CrmLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/crm");
    } else {
      toast.error(res.error);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPassword(email);
      setForgotSent(true);
      setDevLink(res.reset_link || "");
      toast.success("If an account exists, a reset link is on its way.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-navy-light/40 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gold-light hover:text-gold text-sm mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <img src={logo} alt="Rehabilitated 4 Life" className="h-20 w-auto" data-testid="crm-login-logo" />
            <h1 className="mt-4 font-serif text-2xl font-bold text-navy">
              Back Office CRM
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Sign in to manage leads, clients &amp; appointments
            </p>
          </div>

          {forgot ? (
            forgotSent ? (
              <div className="space-y-3 text-center" data-testid="crm-forgot-sent">
                <p className="text-sm text-gray-600">
                  If an account exists for that email, a reset link has been sent.
                </p>
                {devLink && (
                  <div className="bg-cream rounded-lg p-3 text-xs text-gray-600 break-all">
                    <span className="font-medium text-navy">Dev link (email not configured): </span>
                    <a href={devLink} className="text-gold-dark underline" data-testid="crm-forgot-dev-link">{devLink}</a>
                  </div>
                )}
                <button type="button" onClick={() => { setForgot(false); setForgotSent(false); setDevLink(""); }} className="text-sm text-navy hover:underline">
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-4" data-testid="crm-forgot-form">
                <p className="text-sm text-gray-500">Enter your email to receive a password reset link.</p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10"
                    data-testid="crm-forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-navy hover:bg-navy-light text-white font-semibold h-11" data-testid="crm-forgot-submit-button">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <button type="button" onClick={() => setForgot(false)} className="w-full text-center text-sm text-gray-500 hover:text-navy">
                  Back to sign in
                </button>
              </form>
            )
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="text-right">
              <button type="button" onClick={() => setForgot(true)} className="text-xs text-gold-dark hover:underline" data-testid="crm-forgot-password-link">
                Forgot password?
              </button>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-navy hover:bg-navy-light text-white font-semibold h-11"
            >
              {loading ? "Signing in..." : (<><LogIn className="h-4 w-4 mr-1.5" /> Sign In</>)}
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
